import type { SubscriptionDetails } from "schemas/generated/billing";
import type { ReploProject } from "schemas/generated/project";
import type { User } from "schemas/generated/user";

import { getWebInstrumentations, initializeFaro } from "@grafana/faro-react";
import { AnalyticsBrowser } from "@segment/analytics-next";
import * as Sentry from "@sentry/react";
import { extractDomainFromEmail } from "@v2/utils/email";
import { getProjectName, getStoreData } from "@v2/utils/project-utils";
import posthog from "posthog-js";
import { sum } from "replo-utils/lib/array";
import { ANALYTICS_GROUP_NAMES } from "replo-utils/lib/misc/analytics";
import {
  errorUserFacingDetailsSchema,
  extractErrorTrackingEventDetails,
  shouldReportErrorToSentry,
} from "schemas/errors";

import {
  segmentAnalytics,
  setSegmentAnalytics,
  shouldLogAnalytics,
} from "./tracking";

function extractUserFacingDetailsFromUnknownError(error: unknown) {
  if (
    typeof error === "object" &&
    error !== null &&
    "error" in error &&
    typeof error.error === "object" &&
    error.error !== null &&
    "data" in error.error
  ) {
    const userFacingDetails = errorUserFacingDetailsSchema.safeParse(
      error.error.data,
    );
    if (userFacingDetails.success) {
      return userFacingDetails.data;
    }
  }
}

export const initializeErrorTracking = () => {
  Sentry.init({
    dsn: "https://2f4d0ccb0334467d8b95ffe173042c23@o1267142.ingest.sentry.io/6453263",
    // Disable tracing and profiling since we don't use them currently
    tracesSampleRate: 0,
    profilesSampleRate: 0,
    release: window.SENTRY_RELEASE?.id,
    beforeSend: (event, hint) => {
      const error = hint.originalException;

      // NOTE (Gabe 2024-09-05): If there are user facing details then we
      // shouldn't send this error to Sentry.
      if (extractUserFacingDetailsFromUnknownError(error)) {
        return null;
      }

      if (error instanceof Error && !shouldReportErrorToSentry(error)) {
        return null;
      }

      if (event.message?.match(/resizeobserver loop limit exceeded/i)) {
        // Note (Noah, 2022-07-25): This event doesn't actually indicate a
        // problem, so we filter it out
        // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
        return null;
      }
      // Note (Noah, 2022-08-25, REPL-3764): There are several shopify apps which try to mess
      // with the history when they're loaded by mirror into the Canvas srcdoc iframe, and because
      // of cross domain security restrictions, they get an error. This doesn't affect anything,
      // so we filter it from our error logs
      if (
        event.message?.match(/A history state object with URL 'about:srcdoc'/)
      ) {
        return null;
      }

      // NOTE (Nick, 2026-02-02, REPL-23284): Filter PostHog rrweb session recording errors.
      // These are benign errors from rrweb trying to access cross-origin iframes or DOM nodes
      // that were removed during React reconciliation. They don't affect core editor functionality.
      const rrwebErrorPatterns = [
        // Cross-origin iframe access errors (addEventListener/removeEventListener blocked)
        /blocked a frame with origin .* from accessing a cross-origin frame/i,
        // Tainted canvas toDataURL errors (backup filter if captureCanvas: { recordCanvas: false } doesn't catch all)
        /tainted canvases may not be exported/i,
        // Orphaned DOM node errors (race condition with React reconciliation)
        /the given node has no parent/i,
        // Generic rrweb SecurityError pattern
        /failed to read a named property '(addeventlistener|removeeventlistener)' from 'window'/i,
      ];
      const errorMessage =
        event.message ?? event.exception?.values?.[0]?.value ?? "";
      if (rrwebErrorPatterns.some((pattern) => pattern.test(errorMessage))) {
        return null;
      }

      const details = extractErrorTrackingEventDetails(
        error,
        event.transaction,
      );

      return {
        ...event,
        tags: {
          ...event.tags,
          ...details?.tags,
        },
        fingerprint: details ? details.fingerprint : ["{{ default }}"],
      };
    },
  });
};

export const initializePosthogIfNeeded = () => {
  if (!shouldLogAnalytics()) {
    return;
  }

  // Note (Noah, 2023-01-18): Add posthog here to help debug issues with feature
  // flags, etc in the editor. Note that we can't use getAlchemyEditorWindow because
  // by the time this runs, window.alchemyEditor might not have been initialized yet
  if (typeof window !== "undefined") {
    window.reploPosthog = posthog;
  }

  if (posthog.config.token === "") {
    posthog.init("phc_dRETsj85vNkKrvQQszEFRBibH5kUF9l7rbjACuKPanZ", {
      api_host: "https://d99bbtllqsaif.cloudfront.net",
      autocapture: false,
      rageclick: false,
      capture_pageview: true,
      capture_pageleave: false,
      disable_session_recording: true, // Disable initially
      persistence: "localStorage+cookie",
      loaded: function (posthog) {
        posthog.config.disable_session_recording = !shouldLogAnalytics();
      },
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: {
          password: true,
        },
        // NOTE (Nick, 2025-12-15): Enable cross-origin iframe recording so PostHog can capture activity inside the Daytona sandbox iframe.
        // This requires the PostHog child script to be injected in the iframe (see posthog-iframe-relay.ts in scaffold).
        recordCrossOriginIframes: true,
        // NOTE (Nick, 2026-02-02, REPL-23284): Disable canvas recording to prevent SecurityError on tainted canvases.
        // Canvases with cross-origin images (e.g., Shopify CDN without CORS) throw "Tainted canvases may not be exported"
        // when rrweb tries to serialize them with toDataURL(). Canvas content isn't critical for session replay debugging.
        captureCanvas: { recordCanvas: false },
      },
    });
  }
};

export const initializeAnalytics = () => {
  if (!shouldLogAnalytics()) {
    return;
  }
  initializePosthogIfNeeded();
  setSegmentAnalytics(
    AnalyticsBrowser.load({
      writeKey: "FZO6ACG1KkVRwPZXvGPToYQTVhjm5WOH",
    }),
  );
};

export const initUserBasedAnalytics = (user: User) => {
  if (!shouldLogAnalytics()) {
    return;
  }

  Sentry.setUser({ email: user.email });

  void segmentAnalytics?.identify(user.email, {
    displayName: user.name,
    email: user.email,
    verified: true,
  });

  posthog.identify(user.email, {
    displayName: user.name,
    email: user.email,
  });

  const uniqueDomain = extractDomainFromEmail(user.email);
  if (uniqueDomain) {
    void segmentAnalytics?.group(`domain:${uniqueDomain}`, {
      type: "domain",
      name: uniqueDomain,
    });
    void posthog?.group(ANALYTICS_GROUP_NAMES.domain, uniqueDomain, {
      type: "domain",
      name: uniqueDomain,
    });
  }
};

/**
 * Extracts billing details from subscription info for analytics group properties.
 * Used by both project and workspace group initialization.
 */
function extractBillingDetails(
  subscriptionInfo: SubscriptionDetails | undefined,
) {
  if (!subscriptionInfo) {
    return {
      billingPlanMonthlyAmount: undefined,
      billingPlanStartedAt: undefined,
      paymentProcessor: undefined,
      plan: "free" as const,
    };
  }

  const billingPlanMonthlyAmount = sum(
    subscriptionInfo.productUnits.map((unit) => unit.monthlyAmount),
  );

  return {
    billingPlanMonthlyAmount,
    billingPlanStartedAt: subscriptionInfo.startedAt,
    paymentProcessor: subscriptionInfo.paymentProcessor,
    plan: subscriptionInfo.billingTier ?? ("free" as const),
  };
}

export const initProjectBasedAnalytics = (
  project: ReploProject,
  subscriptionInfo: SubscriptionDetails | undefined,
) => {
  if (!shouldLogAnalytics()) {
    return false;
  }

  const billingDetails = extractBillingDetails(subscriptionInfo);
  const shopifyUrl = getStoreData(project)?.shopifyUrl;

  const commonProperties = {
    name: getProjectName(project),
    shopifyShopUrl: shopifyUrl,
    tags: [`org_${billingDetails.plan}`],
    tenancy: project.tenancy,
    referrerCode: project.referrerCode?.code,
    billingPlanMonthlyAmount: billingDetails.billingPlanMonthlyAmount,
    billingPlanStartedAt: billingDetails.billingPlanStartedAt,
    paymentProcessor: billingDetails.paymentProcessor,
  };

  if (shopifyUrl) {
    void segmentAnalytics?.group(`store:${shopifyUrl}`, {
      type: "store",
      ...commonProperties,
    });
  }

  void posthog?.group(ANALYTICS_GROUP_NAMES.project, project.id, {
    plan: billingDetails.plan,
    ...commonProperties,
  });
  if (project.ownerWorkspaceId) {
    void posthog?.group(
      ANALYTICS_GROUP_NAMES.workspace,
      project.ownerWorkspaceId,
      {
        id: project.ownerWorkspaceId,
        name: project.ownerWorkspace?.name,
      },
    );
  }

  return true;
};

export const initWorkspaceBasedAnalytics = ({
  id,
  name,
  subscriptionInfo,
}: {
  id: string;
  name: string;
  subscriptionInfo?: SubscriptionDetails;
}) => {
  if (!shouldLogAnalytics()) {
    return;
  }

  const billingDetails = extractBillingDetails(subscriptionInfo);

  void posthog?.group(ANALYTICS_GROUP_NAMES.workspace, id, {
    type: ANALYTICS_GROUP_NAMES.workspace,
    id,
    name,
    plan: billingDetails.plan,
    billingPlanMonthlyAmount: billingDetails.billingPlanMonthlyAmount,
    billingPlanStartedAt: billingDetails.billingPlanStartedAt,
    paymentProcessor: billingDetails.paymentProcessor,
    tags: [`org_${billingDetails.plan}`],
  });
};

export function initializePerformanceMonitoring() {
  if (!shouldLogAnalytics()) {
    return;
  }

  initializeFaro({
    url: "https://faro-collector-prod-us-west-0.grafana.net/collect/6a644c89636725ec3c696b0392415764",
    app: {
      name: "editor",
      version: "1.0.0",
      environment: "production",
    },
    instrumentations: [
      ...getWebInstrumentations({
        enablePerformanceInstrumentation: false,
        captureConsole: false,
      }),
    ],
  });
}
